import axios from 'axios';

/**
 * Resolve the backend URL.
 *
 * Build-time env var (`REACT_APP_BACKEND_URL`) is the primary source. CRA
 * bakes it in at build time, so if a build was deployed with the wrong value
 * (e.g. a stale preview URL pointing at `*.emergent.host`) the frontend can
 * end up calling the wrong host and CORS-failing.
 *
 * Defence in depth: at runtime, if the env-var-baked URL is on a different
 * origin than the page is being served from, fall back to the page's own
 * origin. Production deployments on a custom domain serve frontend +
 * backend (`/api/*`) from the same origin, so `window.location.origin` is
 * always the right answer in the browser.
 *
 * The env var still wins in local dev (where preview frontend runs on :3000
 * and the backend on a separate preview host).
 */
function resolveBackendUrl() {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (typeof window === 'undefined') return envUrl;
  const pageOrigin = window.location.origin;
  // Local dev (localhost / 127.0.0.1) → trust the env var
  if (pageOrigin.includes('localhost') || pageOrigin.includes('127.0.0.1')) return envUrl;
  // Preview emergentagent.com domains → trust the env var (it matches)
  if (pageOrigin.includes('preview.emergentagent.com')) return envUrl;
  // Otherwise (custom domain in production), use the same origin as the page.
  // This survives stale builds where REACT_APP_BACKEND_URL still points at an
  // old preview/emergent.host backend.
  return pageOrigin;
}

const BACKEND_URL = resolveBackendUrl();
const API_BASE = `${BACKEND_URL}/api`;

// Exported for components that build URLs directly (PDF downloads, window.open, fetch, etc.)
export { BACKEND_URL };

// Create axios instance with timeout
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors and network issues
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    // Public Sandbox: backend returns 403 with `sandbox: true` for any
    // attempt to mutate data. Surface a friendly toast so the demo user
    // understands why their action didn't go through, and where to go next.
    if (
      error.response?.status === 403 &&
      error.response?.data?.sandbox === true &&
      typeof window !== 'undefined'
    ) {
      try {
        // Avoid spamming if multiple writes fire from the same form submit
        if (!window.__sandboxToastShownAt || Date.now() - window.__sandboxToastShownAt > 1500) {
          window.__sandboxToastShownAt = Date.now();
          // Lazy-load sonner so this file stays import-clean
          import('sonner').then(({ toast }) => {
            toast.error(error.response.data.detail || 'Public Sandbox is read-only.', {
              description: 'Saving, editing and deleting are disabled in the demo. Browse and search freely.',
              action: error.response.data.cta_url ? {
                label: error.response.data.cta_label || 'Talk to us',
                onClick: () => { window.location.href = error.response.data.cta_url; },
              } : undefined,
              duration: 6000,
            });
          }).catch(() => {});
        }
      } catch (_) { /* never let the toast itself break the flow */ }
    }
    // Log network errors for debugging
    if (!error.response) {
      console.error('Network error:', error.message);
    }
    return Promise.reject(error);
  }
);

// Auth
export const checkSetup = () => api.get('/auth/check-setup');
export const setupTenant = (tenantData, adminData) => 
  api.post('/auth/setup-tenant', { 
    name: tenantData.name,
    admin_name: adminData.name,
    email: adminData.email,
    password: adminData.password,
    phone: adminData.phone
  });
export const login = (data) => api.post('/auth/login', data);
export const getMe = () => api.get('/auth/me');
export const changePassword = (data) => api.post('/auth/change-password', data);
export const setupMFA = () => api.post('/auth/mfa/setup');
export const verifyMFA = (code) => api.post('/auth/mfa/verify', { code });
export const disableMFA = (code) => api.post('/auth/mfa/disable', { code });
export const trustDevice = () => api.post('/auth/trust-device');
export const requestMagicLink = (data) => api.post('/auth/request-magic-link', data);
export const verifyMagicLink = (token) => api.post(`/auth/verify-magic-link?token=${token}`);
export const refreshToken = () => api.post('/auth/refresh');
export const getSessionInfo = () => api.get('/auth/session-info');

// Users
export const getUsers = () => api.get('/users');
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const resetUserPassword = (id) => api.post(`/users/${id}/reset-password`);
export const resetUserMFA = (id) => api.post(`/users/${id}/reset-mfa`);
export const sendUserCredentials = (id) => api.post(`/users/${id}/send-credentials`);

// Customers
export const getCustomers = (params) => api.get('/customers', { params });
export const getCustomer = (id) => api.get(`/customers/${id}`);
export const createCustomer = (data) => api.post('/customers', data);
export const updateCustomer = (id, data) => api.put(`/customers/${id}`, data);
export const deleteCustomer = (id) => api.delete(`/customers/${id}`);
export const checkCustomerDependencies = (id) => api.get(`/customers/${id}/dependencies`);
export const deleteCustomerCascade = (id, data) => api.post(`/customers/${id}/delete-cascade`, data);
export const importCustomers = (file, strict = false) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/customers/import?strict=${strict}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getCustomerAddresses = (customerId) => api.get(`/customers/${customerId}/addresses`);
export const addCustomerAddress = (customerId, data) => api.post(`/customers/${customerId}/addresses`, data);
export const updateCustomerAddress = (customerId, addressId, data) => api.put(`/customers/${customerId}/addresses/${addressId}`, data);
export const deleteCustomerAddress = (customerId, addressId) => api.delete(`/customers/${customerId}/addresses/${addressId}`);
export const exportCustomers = () => `${API_BASE}/customers/export`;

// Reference Data Import (Products, Co-Branding, Customers from single file)
export const importReferenceData = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/import/reference-data', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Co-Branding
export const getCoBrandings = (params) => api.get('/cobranding', { params });
export const getCoBranding = (id) => api.get(`/cobranding/${id}`);
export const createCoBranding = (data) => api.post('/cobranding', data);
export const updateCoBranding = (id, data) => api.put(`/cobranding/${id}`, data);
export const deleteCoBranding = (id) => api.delete(`/cobranding/${id}`);
export const bulkDeleteCoBranding = (ids) => api.post('/cobranding/bulk-delete', { ids });

// Customer Product Mappings (Supplier Product Codes)
export const getCustomerProductMappings = (params) => api.get('/customer-product-mappings', { params });
export const getCustomerProductMapping = (id) => api.get(`/customer-product-mappings/${id}`);
export const createCustomerProductMapping = (data) => api.post('/customer-product-mappings', data);
export const updateCustomerProductMapping = (id, data) => api.put(`/customer-product-mappings/${id}`, data);
export const deactivateCustomerProductMapping = (id) => api.delete(`/customer-product-mappings/${id}`);
export const lookupSupplierProductCode = (customerId, productId) => 
  api.get('/customer-product-mappings/lookup', { params: { customer_id: customerId, product_id: productId } });
export const getCustomerMappingsLookup = (customerId) => 
  api.get(`/customer-product-mappings/by-customer/${customerId}`);

// Products
export const getProducts = (params) => api.get('/products', { params });
export const getProduct = (id) => api.get(`/products/${id}`);
export const createProduct = (data) => api.post('/products', data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/products/${id}`);
export const checkProductDependencies = (id) => api.get(`/products/${id}/dependencies`);
export const deleteProductCascade = (id, data) => api.post(`/products/${id}/delete-cascade`, data);
export const bulkUpdateProductCategories = (productIds, categories, action = 'set') => 
  api.post('/products/bulk-categories', { product_ids: productIds, categories, action });
export const importProducts = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/products/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Product calculation factors
export const learnProductFactors = (productId, data) => api.post(`/products/${productId}/learn-factors`, data);
export const calculateProductValues = (productId, params) => api.get(`/products/${productId}/calculate`, { params });

// Templates
export const getCustomerTemplate = () => `${API_BASE}/templates/customers`;
export const getProductTemplate = () => `${API_BASE}/templates/products`;

// Batches
export const getBatches = (params) => api.get('/batches', { params });
export const getBatch = (id) => api.get(`/batches/${id}`);
export const createBatch = (data) => api.post('/batches', data);
export const updateBatch = (id, data) => api.put(`/batches/${id}`, data);
export const deleteBatch = (id) => api.delete(`/batches/${id}`);
export const getBatchTraceability = (id) => api.get(`/batches/${id}/traceability`);
export const updateBatchQCStatus = (id, data) => api.post(`/batches/${id}/qc-status`, data);
export const getAvailableBatches = (params) => api.get('/batches/available', { params });

// Transformations (Product Splitting/Cutting)
export const getTransformations = (params) => api.get('/transformations', { params });
export const getTransformation = (id) => api.get(`/transformations/${id}`);
export const createTransformation = (data) => api.post('/transformations', data);

// Product children (for master/child relationship)
export const getChildProducts = (productId) => api.get(`/products/${productId}/children`);

// Dispatches
export const getDispatches = (params) => api.get('/dispatches', { params });
export const getNextDispatchNumber = () => api.get('/dispatches/next-number');
export const getDispatch = (id) => api.get(`/dispatches/${id}`);
export const createDispatch = (data) => api.post('/dispatches', data);
export const updateDispatch = (id, data) => api.put(`/dispatches/${id}`, data);
export const lockDispatch = (id) => api.post(`/dispatches/${id}/lock`);
export const unlockDispatch = (id, reason) => api.post(`/dispatches/${id}/unlock`, null, { params: { reason } });
export const markDispatched = (id, upliftDate) =>
  api.post(`/dispatches/${id}/dispatch${upliftDate ? `?uplift_date=${encodeURIComponent(upliftDate)}` : ''}`);
export const updateDispatchStatus = (id, status, batchLines, dispatchLines, upliftDate) =>
  api.post(`/dispatches/${id}/status`, {
    status,
    batch_lines: batchLines || undefined,
    dispatch_lines: dispatchLines || undefined,
    uplift_date: upliftDate || undefined,
  });
export const softDeleteDispatch = (id, data) => api.post(`/dispatches/${id}/delete`, data);
export const getDeletedDispatches = (params) => api.get('/dispatches/deleted', { params });
export const restoreDispatch = (id) => api.post(`/dispatches/${id}/restore`);
export const getDispatchEditHistory = (id) => api.get(`/dispatches/${id}/edit-history`);
export const getDispatchEmailTemplate = (id) => api.get(`/dispatches/${id}/email-template`);
export const sendDispatchEmail = (id, data) => api.post(`/dispatches/${id}/send-email`, data);
export const getDispatchPDF = (id) => {
  const token = localStorage.getItem('token');
  return `${API_BASE}/dispatches/${id}/download-pdf?token=${encodeURIComponent(token)}`;
};
export const printLabel = (dispatchId, lineId) => 
  api.post(`/dispatches/${dispatchId}/lines/${lineId}/print-label`);

// Order Pipeline - Status Management
export const checkOrderStatusChange = (orderId, targetStatus) => 
  api.get(`/orders/${orderId}/status-check`, { params: { target_status: targetStatus } });
export const revertOrderStatus = (orderId, data) => 
  api.post(`/orders/${orderId}/revert-status`, data);

// Batches - soft delete
export const softDeleteBatch = (id, data) => api.post(`/batches/${id}/delete`, data);
export const getDeletedBatches = (params) => api.get('/batches/deleted', { params });
export const restoreBatch = (id) => api.post(`/batches/${id}/restore`);
export const bulkDeleteBatches = (batchIds) => api.post('/batches/bulk-delete', { batch_ids: batchIds });
export const hardDeleteBatch = (id) => api.delete(`/batches/${id}`);

// Recall
export const searchRecall = (data) => api.post('/recall/search', data);
export const exportRecall = (params) => 
  `${API_BASE}/recall/export?${new URLSearchParams(params).toString()}`;

// Reports
export const getWeeklyOrdersReport = (params) => api.get('/reports/weekly-orders', { params });
export const getDispatchSummaryReport = (params) => api.get('/reports/dispatch-summary', { params });
export const getBatchRegisterReport = () => api.get('/reports/batch-register');
export const getAuditLogReport = (params) => api.get('/reports/audit-log', { params });
export const getAuditLogFilters = () => api.get('/reports/audit-log/filters');
export const getAuditLogStats = () => api.get('/reports/audit-log/stats');
export const cleanupAuditLogs = () => api.post('/reports/audit-log/cleanup');
export const getLabelPrintsReport = (params) => api.get('/reports/label-prints', { params });
export const getTraceabilityReport = (params) => api.get('/reports/traceability', { params });
export const getQCStatusReport = (params) => api.get('/reports/qc-status', { params });
export const bulkApproveQC = (data) => api.post('/reports/qc-status/bulk-approve', data);

// Settings
export const getSettings = () => api.get('/settings');
export const updateSettings = (data) => api.put('/settings', data);
export const getProductFormats = () => api.get('/settings/product-formats');
export const getProductCategories = () => api.get('/settings/product-categories');
export const getTransportOptions = () => api.get('/settings/transport-options');
export const testSMTP = (email) => api.post('/settings/smtp-test', { test_email: email });

// Permissions
export const getPermissionsList = () => api.get('/permissions/list');
export const updateUserPermissions = (userId, permissions) => api.put(`/users/${userId}/permissions`, permissions);

// Customer Product Mappings - Reverse lookup
export const reverseLookupSupplierCode = (customerId, supplierCode) => 
  api.get(`/customer-product-mappings/reverse-lookup?customer_id=${customerId}&supplier_code=${encodeURIComponent(supplierCode)}`);

// Backups
// Custom Roles
export const getCustomRoles = () => api.get('/roles/custom');
export const createCustomRole = (data) => api.post('/roles/custom', data);
export const updateCustomRole = (id, data) => api.put(`/roles/custom/${id}`, data);
export const deleteCustomRole = (id) => api.delete(`/roles/custom/${id}`);

export const getBackups = () => api.get('/backups');
export const createBackup = (data) => api.post('/backups', data || {});
export const getBackup = (id) => api.get(`/backups/${id}`);
export const previewRestore = (id) => api.get(`/backups/${id}/preview`);
export const uploadPreview = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/backups/upload-preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const restoreBackup = (id, data) => api.post(`/backups/${id}/restore`, data);
export const deleteBackup = (id) => api.delete(`/backups/${id}`);
export const downloadBackup = (id) => api.get(`/backups/${id}/download`, { responseType: 'blob', timeout: 120000 });
export const exportBackupFile = () => api.post('/backups/export-file', {}, { responseType: 'blob', timeout: 120000 });
export const restoreFromFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/backups/restore-file', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getBackupSettings = () => api.get('/backup-settings');
export const updateBackupSettings = (data) => api.put('/backup-settings', data);
export const getBackupHealth = () => api.get('/backup-health');

// FTP Backup
export const getFTPSettings = () => api.get('/ftp-backup/settings');
export const saveFTPSettings = (data) => api.post('/ftp-backup/settings', data);
export const testFTPConnection = (data) => api.post('/ftp-backup/test-connection', data);
export const createMongodumpBackup = () => api.post('/ftp-backup/create');
export const getLocalBackups = () => api.get('/ftp-backup/local');
export const getRemoteBackups = () => api.get('/ftp-backup/remote');
export const restoreFTPBackup = (id) => api.post(`/ftp-backup/restore/${id}`);
export const restoreRemoteBackup = (path) => api.post('/ftp-backup/restore-remote', { path });
export const deleteFTPBackup = (id) => api.delete(`/ftp-backup/${id}`);
export const getFTPSchedule = () => api.get('/ftp-backup/schedule');
export const updateFTPSchedule = (data) => api.put('/ftp-backup/schedule', data);

// Order Lock Status
export const getOrderLockStatus = (dispatchId) => api.get(`/dispatches/${dispatchId}/lock-status`);

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

// Reports
export const getSalesByCategoryReport = (params) => api.get('/reports/sales-by-category', { params });
export const getSalesByCategoryPdfUrl = (params, token) => {
  const queryString = new URLSearchParams({ ...params, token }).toString();
  return `${BACKEND_URL}/api/reports/sales-by-category/pdf?${queryString}`;
};

// Recall Reporting
export const getRecallByBatch = (batchCode) => api.get('/reports/recall/by-batch', { params: { batch_code: batchCode } });
export const getRecallByUseByDate = (useByFrom, useByTo) => api.get('/reports/recall/by-use-by-date', { 
  params: { use_by_from: useByFrom, use_by_to: useByTo || useByFrom } 
});
export const getRecallByBatchPdfUrl = (batchCode, token) => {
  return `${BACKEND_URL}/api/reports/recall/by-batch/pdf?batch_code=${encodeURIComponent(batchCode)}&token=${encodeURIComponent(token)}`;
};
export const getRecallByUseByDatePdfUrl = (useByFrom, useByTo, token) => {
  const params = new URLSearchParams({ use_by_from: useByFrom, token });
  if (useByTo) params.append('use_by_to', useByTo);
  return `${BACKEND_URL}/api/reports/recall/by-use-by-date/pdf?${params.toString()}`;
};

// Send recall email notification
export const sendRecallEmail = (data) => api.post('/reports/recall/send-email', data);

// Pallet Labels
export const getPalletLabelsUrl = (dispatchId, token, copies = 2) => {
  return `${BACKEND_URL}/api/dispatches/${dispatchId}/pallet-labels?copies=${copies}&token=${encodeURIComponent(token)}`;
};

// Transport Report
export const getTransportReportUrl = (params, token) => {
  const queryString = new URLSearchParams({ ...params, token }).toString();
  return `${BACKEND_URL}/api/reports/transport?${queryString}`;
};
export const getTransportReport = (params) => api.get('/reports/transport', { params, responseType: 'blob', timeout: 60000 });

// Admin utilities
export const backfillSupplierCodes = () => api.post('/admin/backfill-supplier-codes');
export const purgeInactiveMappings = () => api.post('/customer-product-mappings/purge-inactive');
export const getInactiveMappingCount = () => api.get('/customer-product-mappings/inactive-count');

// Conversion formula
export const lookupFormula = (customerId, productId, cobrandingId) =>
  api.get('/customer-product-mappings/formula/lookup', { params: { customer_id: customerId, product_id: productId, cobranding_id: cobrandingId || undefined } });
export const updateFormula = (mappingId, data) =>
  api.put(`/customer-product-mappings/${mappingId}/formula`, data);
export const learnFormula = (data) =>
  api.post('/customer-product-mappings/formula/learn', data);
export const scanOrderFormulas = () =>
  api.post('/admin/scan-order-formulas');

// Account self-service
export const updateProfile = (data) => api.put('/account/profile', data);

// Operators
export const getOperators = () => api.get('/operators');
export const createOperator = (data) => api.post('/operators', data);
export const updateOperator = (id, data) => api.put(`/operators/${id}`, data);
export const deleteOperator = (id) => api.delete(`/operators/${id}`);
export const verifyOperatorCode = (code) => api.post('/operators/verify', { code });

// API Keys
export const getApiKeys = () => api.get('/api-keys');
export const createApiKey = (data) => api.post('/api-keys', data);
export const revokeApiKey = (id) => api.delete(`/api-keys/${id}`);

// Compliance
export const getComplianceLogs = (params) => api.get('/compliance/logs', { params });
export const getComplianceSummary = () => api.get('/compliance/summary');
export const getComplianceActions = () => api.get('/compliance/actions');
export const generateComplianceReport = (params) => api.post('/compliance/generate-report', null, { params });
export const listComplianceReports = (params) => api.get('/compliance/reports', { params });
export const downloadComplianceReport = (id) => api.get(`/compliance/download-report/${id}`, { responseType: 'blob' });
export const verifyComplianceChain = () => api.get('/compliance/verify-chain');

// Security Audit Dashboard
export const getSecuritySummary = (range) => api.get('/security-audit/summary', { params: { time_range: range } });
export const getLoginHeatmap = (range) => api.get('/security-audit/login-heatmap', { params: { time_range: range } });
export const getSecurityEvents = (range, type, page) => api.get('/security-audit/events', { params: { time_range: range, event_type: type, page } });
export const getActiveLockouts = () => api.get('/security-audit/lockouts');
export const unlockAccount = (email) => api.post('/security-audit/unlock', null, { params: { email } });
export const getSecurityAnomalies = (range) => api.get('/security-audit/anomalies', { params: { time_range: range } });
export const getTopIPs = (range) => api.get('/security-audit/top-ips', { params: { time_range: range } });

// Order Purge
export const searchOrdersForPurge = (q) => api.get('/order-purge/search', { params: { q } });
export const purgeOrder = (dispatch_id, reason) => api.post('/order-purge/purge', { dispatch_id, reason });
export const getDeletionHistory = () => api.get('/order-purge/history');

// ── Inwards / Production / Traceability (Phase 1) ───────────────────────
// Suppliers
export const getSuppliers = (params) => api.get('/suppliers', { params });
export const createSupplier = (data) => api.post('/suppliers', data);
export const updateSupplier = (id, data) => api.put(`/suppliers/${id}`, data);
export const deleteSupplier = (id) => api.delete(`/suppliers/${id}`);
// Supplier product codes
export const getSupplierCodes = (params) => api.get('/supplier-product-codes', { params });
export const suggestSupplierCode = (supplier_id, internal_item) =>
  api.get('/supplier-product-codes/suggest', { params: { supplier_id, internal_item } });
export const createSupplierCode = (data) => api.post('/supplier-product-codes', data);
export const updateSupplierCode = (id, data) => api.put(`/supplier-product-codes/${id}`, data);
export const deleteSupplierCode = (id) => api.delete(`/supplier-product-codes/${id}`);
// Inward deliveries
export const getInwardDeliveries = (params) => api.get('/inward-deliveries', { params });
export const getInwardDelivery = (id) => api.get(`/inward-deliveries/${id}`);
export const createInwardDelivery = (data) => api.post('/inward-deliveries', data);
export const updateInwardDelivery = (id, data) => api.put(`/inward-deliveries/${id}`, data);
// Milk intake
export const getMilkIntake = (params) => api.get('/milk-intake', { params });
export const createMilkIntake = (data) => api.post('/milk-intake', data);
export const updateMilkIntake = (id, data) => api.put(`/milk-intake/${id}`, data);
// Silos & movements
export const getSilos = (params) => api.get('/silos', { params });
export const createSilo = (data) => api.post('/silos', data);
export const updateSilo = (id, data) => api.put(`/silos/${id}`, data);
export const getSiloMovements = (params) => api.get('/silos/movements', { params });
// Make Days
export const getMakeDays = (params) => api.get('/make-days', { params });
export const getMakeDay = (id) => api.get(`/make-days/${id}`);
export const createMakeDay = (data) => api.post('/make-days', data);
export const updateMakeDay = (id, data) => api.put(`/make-days/${id}`, data);
// Trace
export const backwardTrace = (q) => api.post('/trace/backward', q);
export const forwardTrace = (q) => api.post('/trace/forward', q);
export const recallReport = (q) => api.post('/trace/recall-report', q);
// Traceability events (Phase 3)
export const listTraceEvents = (params) => api.get('/trace/events', { params });
export const getTraceEvent = (id) => api.get(`/trace/events/${id}`);
export const createTraceEvent = (data) => api.post('/trace/events', data);
export const updateTraceEvent = (id, data) => api.put(`/trace/events/${id}`, data);
export const downloadTraceEventPdf = (id) => api.get(`/trace/events/${id}/pdf`, { responseType: 'blob' });
// Demo seed (admin / dev only)
export const seedInwardsDemo = () => api.post('/seed-inwards-demo');

export default api;
